import type { ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import { redirect, type LoaderFunctionArgs } from "react-router";

/**
 * A list URL a page no longer has: a path segment that was a tab and is now a chip, another tab, or
 * the page itself (#6712). `/work/archived` becomes `/work?stage=archived`; `/proposals/current`
 * becomes `/proposals`.
 */
export type RetiredListSegment = Readonly<{
  /** The retired path segment: `archived` in `/work/archived`. */
  segment: string;
  /** The tab's segment it opens now; absent, the page's own path. */
  tab?: string;
  /** The chips it opens now, by group parameter. They replace whatever the link said of those groups. */
  chips?: ListChipSelection;
}>;

/**
 * Where a link to `basePath/segment` opens now, or undefined when `segment` is none of `retired` and
 * the page should handle the URL itself. Every search parameter the link carried is kept, except the
 * chip groups the retired segment now names.
 */
export function retiredListPath(
  basePath: string,
  segment: string | undefined,
  searchParams: URLSearchParams,
  retired: readonly RetiredListSegment[],
): string | undefined {
  if (segment === undefined) return undefined;
  const named = segment.toLowerCase();
  const match = retired.find((each) => each.segment.toLowerCase() === named);
  if (!match) return undefined;

  const next = new URLSearchParams(searchParams);
  for (const [parameter, values] of Object.entries(match.chips ?? {})) {
    next.delete(parameter);
    for (const value of values) next.append(parameter, value);
  }
  const path = match.tab ? `${basePath}/${match.tab}` : basePath;
  const query = next.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * A route loader that sends a retired list URL to where it opens now, and otherwise runs `inner`.
 * Bookmarks, notifications and the assistant's links name the old URLs, so each must still land on
 * the rows it listed. `segmentParameter` is the route parameter the segment is in.
 */
export function retiredListLoader(
  options: Readonly<{
    basePath: string;
    retired: readonly RetiredListSegment[];
    segmentParameter?: string;
    inner?: (args: LoaderFunctionArgs) => Promise<Response | null> | Response | null;
  }>,
) {
  const segmentParameter = options.segmentParameter ?? "pageTab";
  return (args: LoaderFunctionArgs) => {
    const url = new URL(args.request.url);
    const path = retiredListPath(options.basePath, args.params[segmentParameter], url.searchParams, options.retired);
    if (path) return redirect(path);
    return options.inner ? options.inner(args) : null;
  };
}
